import { makeStyles, Button, Spinner } from '@fluentui/react-components';
import { CheckmarkRegular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#FFD600',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '24px',
    overflow: 'hidden',
  },
  warningIcon: {
    width: '120px',
    height: '120px',
    marginBottom: '14px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1a1a1a',
    opacity: 0.5,
    marginBottom: '12px',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a1a1a',
    marginBottom: '56px',
    textAlign: 'center',
  },
  fieldsContainer: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '700px',
    marginBottom: '60px',
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '14px',
    fontSize: '20px',
    color: '#1a1a1a',
    textAlign: 'center',
  },
  fieldLabel: {
    fontWeight: '600',
  },
  buttonArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  confirmButton: {
    minWidth: '200px',
    minHeight: '48px',
    fontSize: '16px',
    fontWeight: '600',
  },
  errorText: {
    color: '#d13438',
    fontSize: '14px',
    fontWeight: '600',
    backgroundColor: 'rgba(255,255,255,0.8)',
    padding: '8px 16px',
    borderRadius: '4px',
  },
  confirmedButton: {
    minWidth: '200px',
    minHeight: '48px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: '#4CAF50',
    color: '#fff',
    ':hover': {
      backgroundColor: '#4CAF50',
      color: '#fff',
    },
  },
  expiredTag: {
    display: 'inline-block',
    backgroundColor: '#d13438',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '700',
    padding: '3px 10px',
    borderRadius: '12px',
    marginLeft: '8px',
    verticalAlign: 'middle',
    letterSpacing: '0.3px',
  },
});

const WarningTriangle = () => (
  <svg viewBox="0 0 100 90" width="120" height="108">
    <polygon
      points="50,5 95,85 5,85"
      fill="#FFD600"
      stroke="#666"
      strokeWidth="3"
      strokeLinejoin="round"
    />
    <polygon
      points="50,12 89,82 11,82"
      fill="#FFD600"
      stroke="none"
    />
    <text
      x="50"
      y="75"
      textAnchor="middle"
      fontSize="55"
      fontWeight="bold"
      fill="#1a1a1a"
      fontFamily="Arial, sans-serif"
    >
      !
    </text>
  </svg>
);

const dateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Montreal',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const parseUtcDate = (isoString) => {
  if (!isoString) return null;
  const normalized = /([Z+-])/.test(isoString.slice(-6)) ? isoString : isoString + 'Z';
  const date = new Date(normalized);
  return isNaN(date.getTime()) ? null : date;
};

const formatAlertDate = (isoString) => {
  const date = parseUtcDate(isoString);
  if (!date) return 'N/A';
  const parts = dateFormatter.formatToParts(date);
  const get = (type) => parts.find(p => p.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}:${get('second')}`;
};

const isExpired = (alert) => {
  const expiresAt = parseUtcDate(alert.expiresAt);
  return expiresAt ? expiresAt.getTime() <= Date.now() : false;
};

const AlertOverlay = ({ alert, onAcknowledge, isAcknowledging, ackError, isConfirmed }) => {
  const styles = useStyles();

  if (!alert) return null;

  const expired = isExpired(alert);

  return (
    <div className={styles.overlay}>
      <div className={styles.warningIcon}>
        <WarningTriangle />
      </div>
      <div className={styles.title}>
        Système de surveillance des recettes
        {expired && <span className={styles.expiredTag}>Expirée</span>}
      </div>
      <div className={styles.subtitle}>{alert.sEquip}</div>
      <div className={styles.fieldsContainer}>
        <div className={styles.fields}>
          <div><span className={styles.fieldLabel}>Date:</span> {formatAlertDate(alert.createdAt)}</div>
          <div><span className={styles.fieldLabel}>Recette 1:</span> {alert.sRecette1 ?? 'N/A'}</div>
          <div><span className={styles.fieldLabel}>Recette 2:</span> {alert.sRecette2 ?? 'N/A'}</div>
        </div>
      </div>
      <div className={styles.buttonArea}>
        <Button
          className={isConfirmed ? styles.confirmedButton : styles.confirmButton}
          appearance={isConfirmed ? 'secondary' : 'primary'}
          disabled={isAcknowledging || isConfirmed}
          icon={isConfirmed ? <CheckmarkRegular /> : isAcknowledging ? <Spinner size="tiny" /> : undefined}
          onClick={() => onAcknowledge(alert.id, alert.iEquip)}
        >
          {isConfirmed ? 'Confirmée' : 'Confirmation'}
        </Button>
        {ackError && <div className={styles.errorText}>{ackError}</div>}
      </div>
    </div>
  );
};

export default AlertOverlay;
